/**
 * The editor drawer as the country page opens it: a container or a master
 * config, and — when a campaign GROUP is open — what a NEW one starts from:
 * a container follows the group's master config (its campaign picked
 * already when the group has one); a master config is the group's (shown,
 * never picked) — blank or copied from another group's master config.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import type { GroupEntry } from '@/features/drop-generator/components/campaign/campaign-scope';
import { ContainerEditorDrawer } from '@/features/drop-generator/components/editor/ContainerEditorDrawer';
import type { EditorTabKey } from '@/features/drop-generator/components/editor/EditorTabs';
import type { DropGenPlanDetail } from '@/features/drop-generator/drop-generator.types';

interface Props {
  plan: DropGenPlanDetail;
  countryName: string;
  containerParam: string | null;
  masterParam: string | null;
  copyParam: string | null;
  editorTab: EditorTabKey | null;
  group: GroupEntry | null;
  setParams: (entries: Record<string, string | null>) => void;
  onOpenMaster: (id: string | 'new', etab?: EditorTabKey) => void;
}

export const CountryEditor: FC<Props> = ({ plan, countryName, containerParam, masterParam, copyParam, editorTab, group, setParams, onOpenMaster }) => {
  if (!containerParam && !masterParam) return null;
  const master = Boolean(masterParam);
  const id = master ? masterParam : containerParam;
  return (
    <ContainerEditorDrawer
      plan={plan}
      countryName={countryName}
      mode={master ? 'master' : 'container'}
      containerId={id === 'new' || id === 'route-test' ? null : id}
      initialTab={editorTab ?? undefined}
      preset={
        master
          ? group && group.members.length > 0
            ? { campaignWiserId: group.members[0].wiserId, name: `${group.members[0].name}${group.members.length > 1 ? ' group' : ''} config`, ...(copyParam ? { copyFromMasterId: copyParam } : {}) }
            : undefined
          : group?.master
            ? {
                masterId: group.master.id,
                ...(group.members.length === 1 ? { campaignWiserId: group.members[0].wiserId } : {}),
                ...(containerParam === 'route-test' ? { kind: 'ROUTE_TEST' as const } : {}),
              }
            : undefined
      }
      campaignLocked={master && group !== null}
      groupKey={group?.key ?? null}
      onClose={() => setParams({ container: null, master: null, etab: null, copy: null })}
      onSaved={(saved) => setParams(master ? { master: saved, copy: null } : { container: saved })}
      onOpenMaster={onOpenMaster}
    />
  );
};
